import React, { useEffect, useMemo, useState } from "react";
import { allProperties } from "../actions/allProperties";
import PropColumn from "../components/PropColumn";
import Footer from "../components/Footer";
import { AiOutlineUnorderedList, AiOutlineBars } from "react-icons/ai";
import { BsCardImage } from "react-icons/bs";
import { cloudinaryUrl } from "../constants/CloudinaryConstants";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SortDropdown from "../components/SortDropdown";
import { sortByAddress } from "../utils/sortHelpers";

const All = () => {
  const [properties, setProperties] = useState(null);
  const [viewMode, setViewMode] = useState("cards"); // "cards" or "table"
  const [previewImage, setPreviewImage] = useState(null);
  const [activeSection, setActiveSection] = useState("all"); // "all" | "res-sale" | "res-lease" | "com-sale" | "com-lease"
  const [sortOrder, setSortOrder] = useState(""); // "", "a-z", "z-a"
  const location = useLocation();
  const navigate = useNavigate();

  // Filters
  const searchParams = new URLSearchParams(location.search);
  const initialQ = searchParams.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const [city, setCity] = useState("");
  const [type, setType] = useState(""); // Residential | Commercial
  const [forFilter, setForFilter] = useState(""); // Sale | Lease
  const [subType, setSubType] = useState(""); // e.g., Single Family, Condo, etc.
  const [county, setCounty] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minBeds, setMinBeds] = useState("");
  const [maxBeds, setMaxBeds] = useState("");
  const [minBaths, setMinBaths] = useState("");
  const [maxBaths, setMaxBaths] = useState("");
  const [minSqft, setMinSqft] = useState("");
  const [maxSqft, setMaxSqft] = useState("");
  const [yearBuilt, setYearBuilt] = useState("");
  const [professionType, setProfessionType] = useState(""); // NEW: Filter by agent profession

  useEffect(() => {
    allProperties({ setProperties });
  }, []);

  // Keep URL in sync when q changes via input (optional)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (q) {
      params.set("q", q);
    } else {
      params.delete("q");
    }
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const filtered = useMemo(() => {
    if (!Array.isArray(properties)) return [];

    // First apply section filter (only Active status)
    let sectionFiltered = properties;
    if (activeSection === "res-sale") {
      sectionFiltered = properties.filter(
        (p) =>
          p.type === "Residential" &&
          p.for === "Sale" &&
          p.property_status === "Active",
      );
    } else if (activeSection === "res-lease") {
      sectionFiltered = properties.filter(
        (p) =>
          p.type === "Residential" &&
          p.for === "Lease" &&
          p.property_status === "Active",
      );
    } else if (activeSection === "com-sale") {
      sectionFiltered = properties.filter(
        (p) =>
          p.type === "Commercial" &&
          p.for === "Sale" &&
          p.property_status === "Active",
      );
    } else if (activeSection === "com-lease") {
      sectionFiltered = properties.filter(
        (p) =>
          p.type === "Commercial" &&
          p.for === "Lease" &&
          p.property_status === "Active",
      );
    } else {
      // Default "all" section - only show Active properties
      sectionFiltered = properties.filter(
        (p) => p.property_status === "Active",
      );
    }

    // Then apply user filters
    let result = sectionFiltered.filter((p) => {
      // keyword filter across property_id and address parts
      const keyword = (q || "").toLowerCase().trim();
      const addr = `${p.street_number || ""} ${p.street_name || ""} ${
        p.city || ""
      } ${p.zip || ""}`.toLowerCase();
      const keyMatch = keyword
        ? String(p.property_id || "")
            .toLowerCase()
            .includes(keyword) || addr.includes(keyword)
        : true;

      const cityMatch = city
        ? (p.city || "").toLowerCase() === city.toLowerCase()
        : true;
      const typeMatch = type
        ? (p.type || "").toLowerCase() === type.toLowerCase()
        : true;
      const forMatch = forFilter
        ? (p.for || "").toLowerCase() === forFilter.toLowerCase()
        : true;
      const subTypeMatch = subType
        ? (p.sub_type || "").toLowerCase() === subType.toLowerCase()
        : true;
      const countyMatch = county
        ? (p.county || "").toLowerCase() === county.toLowerCase()
        : true;
      const minPriceMatch = minPrice
        ? Number(p.price) >= Number(minPrice)
        : true;
      const maxPriceMatch = maxPrice
        ? Number(p.price) <= Number(maxPrice)
        : true;
      const minBedsMatch = minBeds
        ? Number(p.beds_total) >= Number(minBeds)
        : true;
      const maxBedsMatch = maxBeds
        ? Number(p.beds_total) <= Number(maxBeds)
        : true;
      const minBathsMatch = minBaths
        ? Number(p.baths_full) >= Number(minBaths)
        : true;
      const maxBathsMatch = maxBaths
        ? Number(p.baths_full) <= Number(maxBaths)
        : true;
      const minSqftMatch = minSqft
        ? Number(p.living_sqft) >= Number(minSqft)
        : true;
      const maxSqftMatch = maxSqft
        ? Number(p.living_sqft) <= Number(maxSqft)
        : true;
      const yearBuiltMatch = yearBuilt
        ? Number(p.est_year_built) >= Number(yearBuilt)
        : true;
      // NEW: Professional type filter (filter by agent's profession)
      const professionMatch = professionType
        ? (p.agent_class_type || "").toLowerCase() ===
          professionType.toLowerCase()
        : true;

      return (
        keyMatch &&
        cityMatch &&
        typeMatch &&
        forMatch &&
        subTypeMatch &&
        countyMatch &&
        minPriceMatch &&
        maxPriceMatch &&
        minBedsMatch &&
        maxBedsMatch &&
        minBathsMatch &&
        maxBathsMatch &&
        minSqftMatch &&
        maxSqftMatch &&
        yearBuiltMatch &&
        professionMatch
      );
    });

    result = sortByAddress(result, sortOrder);

    return result;
  }, [
    properties,
    q,
    city,
    type,
    forFilter,
    subType,
    county,
    minPrice,
    maxPrice,
    minBeds,
    maxBeds,
    minBaths,
    maxBaths,
    minSqft,
    maxSqft,
    yearBuilt,
    activeSection,
    sortOrder,
    professionType,
  ]);

  const clearFilters = () => {
    setQ("");
    setCity("");
    setType("");
    setForFilter("");
    setSubType("");
    setCounty("");
    setMinPrice("");
    setMaxPrice("");
    setMinBeds("");
    setMaxBeds("");
    setMinBaths("");
    setMaxBaths("");
    setMinSqft("");
    setMaxSqft("");
    setYearBuilt("");
    setProfessionType("");
    setSortOrder("");
  };

  const getSectionTitle = () => {
    switch (activeSection) {
      case "res-sale":
        return "Active Residential Properties For Sale";
      case "res-lease":
        return "Active Residential Properties For Lease";
      case "com-sale":
        return "Active Commercial Properties For Sale";
      case "com-lease":
        return "Active Commercial Properties For Lease";
      default:
        return "All Active Properties";
    }
  };

  return (
    <>
      <body className="min-h-screen flex flex-col bg-slate-50">
        {/* Top Header */}
        <div className="mt-8 mb-6 px-6">
          <h1 className="text-5xl font-bold text-slate-900">
            {getSectionTitle()}
          </h1>
          <p className="text-slate-600 mt-2">
            {filtered?.length || 0} properties available
          </p>
        </div>

        <div className="flex gap-6 px-6 pb-6 flex-1">
          {/* Left Sidebar - Filters */}
          <div className="w-80 flex-shrink-0">
            <div className="sticky top-32 space-y-4">
              {/* View Mode Buttons */}
              <div className="flex gap-2 bg-white rounded-lg p-2 shadow-sm border border-slate-200">
                <button
                  onClick={() => setViewMode("cards")}
                  className={`flex-1 px-3 py-2 rounded flex items-center justify-center gap-2 transition-all font-medium text-sm ${
                    viewMode === "cards"
                      ? "bg-blue-500 text-white"
                      : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                  }`}
                  title="Card View"
                >
                  <AiOutlineUnorderedList size={18} />
                  <span>Cards</span>
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={`flex-1 px-3 py-2 rounded flex items-center justify-center gap-2 transition-all font-medium text-sm ${
                    viewMode === "table"
                      ? "bg-blue-500 text-white"
                      : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                  }`}
                  title="Detailed View"
                >
                  <AiOutlineBars size={18} />
                  <span>Table</span>
                </button>
              </div>

              {/* Section Tabs */}
              <div className="bg-white border border-slate-300 rounded-lg p-3 space-y-3 shadow-sm">
                <button
                  onClick={() => setActiveSection("all")}
                  className={`w-full h-10 px-4 rounded-lg font-semibold transition-all text-sm ${
                    activeSection === "all"
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-white border border-slate-300 text-gray-700 hover:bg-slate-50"
                  }`}
                >
                  All Active
                </button>

                <div className="space-y-2">
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                    Residential
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setActiveSection("res-sale")}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        activeSection === "res-sale"
                          ? "bg-green-600 text-white"
                          : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                      }`}
                    >
                      For Sale
                    </button>
                    <button
                      onClick={() => setActiveSection("res-lease")}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        activeSection === "res-lease"
                          ? "bg-green-600 text-white"
                          : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                      }`}
                    >
                      For Lease
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                    Commercial
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setActiveSection("com-sale")}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        activeSection === "com-sale"
                          ? "bg-green-600 text-white"
                          : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                      }`}
                    >
                      For Sale
                    </button>
                    <button
                      onClick={() => setActiveSection("com-lease")}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        activeSection === "com-lease"
                          ? "bg-green-600 text-white"
                          : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                      }`}
                    >
                      For Lease
                    </button>
                  </div>
                </div>
              </div>

              {/* Advanced Filters */}
              <div className="bg-white border border-slate-300 rounded-lg p-4 space-y-4 shadow-sm">
                {/* Search and Location */}
                <div>
                  <h3 className="text-xs font-bold text-slate-900 mb-2 uppercase tracking-wide">
                    Search & Location
                  </h3>
                  <div className="space-y-2">
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Keyword / Key #
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="text"
                        placeholder="12345 or Main"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        City
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="text"
                        placeholder="City"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        County
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="text"
                        placeholder="County"
                        value={county}
                        onChange={(e) => setCounty(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Sub Type
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="text"
                        placeholder="Condo, House"
                        value={subType}
                        onChange={(e) => setSubType(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Price Range */}
                <div className="border-t border-slate-200 pt-3">
                  <h3 className="text-xs font-bold text-slate-900 mb-3 uppercase tracking-wide">
                    Price
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Min Price
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="number"
                        placeholder="$0"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Max Price
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="number"
                        placeholder="Any"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Beds & Baths */}
                <div className="border-t border-slate-200 pt-3">
                  <h3 className="text-xs font-bold text-slate-900 mb-3 uppercase tracking-wide">
                    Beds & Baths
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Min Beds
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="number"
                        placeholder="Any"
                        value={minBeds}
                        onChange={(e) => setMinBeds(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Max Beds
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="number"
                        placeholder="Any"
                        value={maxBeds}
                        onChange={(e) => setMaxBeds(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Min Baths
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="number"
                        placeholder="Any"
                        value={minBaths}
                        onChange={(e) => setMinBaths(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Max Baths
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="number"
                        placeholder="Any"
                        value={maxBaths}
                        onChange={(e) => setMaxBaths(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Square Footage & Year Built */}
                <div className="border-t border-slate-200 pt-3">
                  <h3 className="text-xs font-bold text-slate-900 mb-3 uppercase tracking-wide">
                    Size & Age
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Min Sqft
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="number"
                        placeholder="Any"
                        value={minSqft}
                        onChange={(e) => setMinSqft(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Max Sqft
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="number"
                        placeholder="Any"
                        value={maxSqft}
                        onChange={(e) => setMaxSqft(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col col-span-2">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Min Year Built
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="number"
                        placeholder="Any"
                        value={yearBuilt}
                        onChange={(e) => setYearBuilt(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Type Filter - Phase 1 */}
                <div className="border-t border-slate-200 pt-3">
                  <h3 className="text-xs font-bold text-slate-900 mb-3 uppercase tracking-wide">
                    Agent Professional Type
                  </h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="professionType"
                        value=""
                        checked={professionType === ""}
                        onChange={(e) => setProfessionType(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-slate-700">
                        All Professionals
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="professionType"
                        value="Realtor"
                        checked={professionType === "Realtor"}
                        onChange={(e) => setProfessionType(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-slate-700">Realtors</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="professionType"
                        value="Builder"
                        checked={professionType === "Builder"}
                        onChange={(e) => setProfessionType(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-slate-700">Builders</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="professionType"
                        value="Title Company"
                        checked={professionType === "Title Company"}
                        onChange={(e) => setProfessionType(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-slate-700">
                        Title Companies
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="professionType"
                        value="Lender"
                        checked={professionType === "Lender"}
                        onChange={(e) => setProfessionType(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-slate-700">Lenders</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="professionType"
                        value="Broker"
                        checked={professionType === "Broker"}
                        onChange={(e) => setProfessionType(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-slate-700">Brokers</span>
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="border-t border-slate-200 pt-3 flex gap-2">
                  <button
                    className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-black transition-all font-medium text-xs"
                    onClick={clearFilters}
                  >
                    Clear All
                  </button>
                  <SortDropdown
                    label="Sort"
                    value={sortOrder}
                    onChange={setSortOrder}
                    options={[
                      { value: "", label: "Default" },
                      { value: "a-z", label: "A - Z" },
                      { value: "z-a", label: "Z - A" },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1">
            {viewMode === "cards" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 relative auto-rows-min items-start justify-center">
                {filtered?.map((property, index) => (
                  <PropColumn
                    key={index}
                    property={property}
                    isFullWidth={true}
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-gray-200 sticky top-0">
                    <tr>
                      <th className="border px-4 py-2 text-center">Key#</th>
                      <th className="border px-4 py-2 text-center">Preview</th>
                      <th className="border px-4 py-2 text-left">Address</th>
                      <th className="border px-4 py-2 text-left">City</th>
                      <th className="border px-4 py-2 text-center">Type</th>
                      <th className="border px-4 py-2 text-center">Beds</th>
                      <th className="border px-4 py-2 text-center">Baths</th>
                      <th className="border px-4 py-2 text-center">
                        Living Sqft
                      </th>
                      <th className="border px-4 py-2 text-right">Price</th>
                      <th className="border px-4 py-2 text-left">For</th>
                      <th className="border px-4 py-2 text-left">Agent</th>
                      <th className="border px-4 py-2 text-center">View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered?.map((property, index) => (
                      <tr key={index} className="hover:bg-gray-100 group">
                        <td className="border px-4 py-2 text-center font-mono">
                          <Link
                            to={`/property/${property.property_id}`}
                            className="text-blue-600 hover:underline"
                          >
                            {property.property_id || "N/A"}
                          </Link>
                        </td>
                        <td className="border px-4 py-2 text-center">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center text-gray-600 hover:text-blue-600"
                            onClick={() => {
                              const url = property?.media_url?.split(",")[0];
                              if (url) setPreviewImage(cloudinaryUrl(url));
                            }}
                          >
                            <BsCardImage size={18} />
                          </button>
                        </td>
                        <td className="border px-4 py-2">
                          {property.street_number} {property.street_name}
                        </td>
                        <td className="border px-4 py-2">{property.city}</td>
                        <td className="border px-4 py-2 text-center">
                          {property.type || "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-center">
                          {property.beds_total || "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-center">
                          {property.baths_full || "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-center">
                          {property.living_sqft
                            ? property.living_sqft.toLocaleString()
                            : "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-right font-semibold">
                          $
                          {property.price
                            ? property.price.toLocaleString()
                            : "N/A"}
                          {property.for === "Lease" ? "/mo" : ""}
                        </td>
                        <td className="border px-4 py-2">
                          {property.for || "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-sm">
                          {property.realtor_name || "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-center">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/property/${property.property_id}`)
                            }
                            className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewImage && (
                  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full mx-4 overflow-hidden">
                      <div className="flex justify-between items-center px-4 py-3 border-b">
                        <h3 className="text-lg font-semibold">Image Preview</h3>
                        <button
                          className="text-gray-600 hover:text-black"
                          onClick={() => setPreviewImage(null)}
                        >
                          Close
                        </button>
                      </div>
                      <div className="bg-black">
                        <img
                          src={previewImage}
                          alt="Property"
                          className="w-full max-h-[70vh] object-contain"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <Footer />
      </body>
    </>
  );
};

export default All;
